const MIN_RECONNECT_MS = 1000
const MAX_RECONNECT_MS = 30000
const EVENT_DEDUPE_WINDOW_MS = 5000
const HEARTBEAT_INTERVAL_MS = 30000
const HEARTBEAT_TIMEOUT_MS = 10000
const HEARTBEAT_MISSES_BEFORE_RECONNECT = 3

const normaliseBaseUrls = (baseUrl) => {
  const inputs = Array.isArray(baseUrl) ? baseUrl : [baseUrl]
  const normalised = inputs
    .map(item => {
      try {
        const raw = String(item ?? '').trim()
        if (!raw || raw === '[object Object]') return null
        const url = new URL(raw)
        if (!['ws:', 'wss:'].includes(url.protocol)) return null
        return url.toString()
      } catch {
        return null
      }
    })
    .filter(Boolean)

  return [...new Set(normalised)]
}

const buildWsUrl = ({ baseUrl, token }) => {
  if (!baseUrl || !token) return null
  try {
    const raw = String(baseUrl).trim()
    if (!raw || raw === '[object Object]') return null

    const url = new URL(raw)
    if (!['ws:', 'wss:'].includes(url.protocol)) return null

    url.searchParams.set('token', token)
    url.searchParams.set('client', 'studio-web')
    return url.toString()
  }
  catch {
    console.error('[studio-realtime] invalid websocket baseUrl', { baseUrl })
    return null
  }
}

export function startStudioRealtime({ baseUrl, token, onEvent, onStatus, onConnected } = {}) {
  const baseUrls = normaliseBaseUrls(baseUrl)
  const connections = new Map()
  const recentEvents = new Map()
  let stopped = false

  const notifyStatus = () => {
    if (typeof onStatus !== 'function') return
    if (!connections.size) {
      onStatus('disconnected')
      return
    }

    const states = [...connections.values()].map(connection => connection.status)
    if (states.some(state => state === 'connected')) {
      onStatus('connected')
      return
    }
    if (states.some(state => state === 'connecting')) {
      onStatus('connecting')
      return
    }
    onStatus('disconnected')
  }

  const clearReconnect = (connection) => {
    if (connection.reconnectTimer) {
      clearTimeout(connection.reconnectTimer)
      connection.reconnectTimer = null
    }
  }

  const clearHeartbeat = (connection) => {
    if (connection.heartbeatTimer) {
      clearTimeout(connection.heartbeatTimer)
      connection.heartbeatTimer = null
    }
    if (connection.heartbeatTimeout) {
      clearTimeout(connection.heartbeatTimeout)
      connection.heartbeatTimeout = null
    }
    connection.awaitingPong = false
  }

  const startHeartbeat = (connection) => {
    clearHeartbeat(connection)

    const sendHeartbeat = () => {
      if (!connection.socket || connection.socket.readyState !== WebSocket.OPEN) return

      connection.awaitingPong = true
      try {
        connection.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }))
      }
      catch {
        connection.socket.close()
        return
      }

      connection.heartbeatTimeout = setTimeout(() => {
        if (!connection.awaitingPong || !connection.socket) return
        connection.awaitingPong = false
        connection.heartbeatMisses += 1
        console.log('[studio-realtime] heartbeat timeout', {
          baseUrl: connection.baseUrl,
          misses: connection.heartbeatMisses,
          threshold: HEARTBEAT_MISSES_BEFORE_RECONNECT,
        })
        if (connection.heartbeatMisses >= HEARTBEAT_MISSES_BEFORE_RECONNECT) {
          connection.closeReason = 'heartbeat-timeout'
          connection.socket.close()
        }
      }, HEARTBEAT_TIMEOUT_MS)

      connection.heartbeatTimer = setTimeout(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
    }

    connection.heartbeatTimer = setTimeout(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
  }

  const scheduleReconnect = (connection) => {
    if (stopped) return
    clearReconnect(connection)
    connection.reconnectTimer = setTimeout(() => connect(connection), connection.retryDelay)
    connection.retryDelay = Math.min(connection.retryDelay * 2, MAX_RECONNECT_MS)
    connection.status = 'connecting'
    notifyStatus()
  }

  const cleanupRecentEvents = () => {
    const now = Date.now()
    for (const [key, timestamp] of recentEvents.entries()) {
      if (now - timestamp > EVENT_DEDUPE_WINDOW_MS) recentEvents.delete(key)
    }
  }

  const getEventDedupKey = (data) => {
    try {
      if (data?.type === 'template_sync_state_updated') {
        const payload = data?.payload ?? {}
        return JSON.stringify([
          data.type,
          payload.client_id ?? data.client_id ?? null,
          payload.template_id ?? null,
          payload.apple_sync_revision ?? null,
          payload.google_sync_revision ?? null,
          payload.apple_check_status ?? null,
          payload.google_check_status ?? null,
          payload.apple_checked_so_far ?? null,
          payload.google_checked_so_far ?? null,
          payload.apple_sync_count ?? null,
          payload.google_sync_count ?? null,
          payload.unique_sync_count ?? null,
        ])
      }

      if (data?.type === 'credit_balance_updated') {
        const payload = data?.payload ?? {}
        return JSON.stringify([
          data.type,
          payload.client_id ?? data.client_id ?? null,
          payload.credits ?? null,
        ])
      }

      return JSON.stringify(data)
    }
    catch {
      return null
    }
  }

  const handleMessage = (data) => {
    if (!data || typeof onEvent !== 'function') return
    cleanupRecentEvents()

    const key = getEventDedupKey(data)
    if (key && recentEvents.has(key)) return
    if (key) recentEvents.set(key, Date.now())

    onEvent(data)
  }

  const connect = (connection) => {
    const url = buildWsUrl({ baseUrl: connection.baseUrl, token })
    if (!url) {
      console.log('[studio-realtime] connect skipped: invalid websocket URL', { baseUrl: connection.baseUrl })
      connection.status = 'disconnected'
      notifyStatus()
      return
    }

    console.log('[studio-realtime] connect', { baseUrl: connection.baseUrl, hasToken: Boolean(token), url })
    connection.status = 'connecting'
    notifyStatus()

    try {
      connection.socket = new WebSocket(url)
    } catch {
      console.log('[studio-realtime] websocket constructor failed, scheduling reconnect', { baseUrl: connection.baseUrl })
      scheduleReconnect(connection)
      return
    }

    connection.socket.addEventListener('open', () => {
      console.log('[studio-realtime] open', { baseUrl: connection.baseUrl })
      connection.retryDelay = MIN_RECONNECT_MS
      connection.status = 'connected'
      connection.heartbeatMisses = 0
      startHeartbeat(connection)
      notifyStatus()
      connection.onConnected?.({
        baseUrl: connection.baseUrl,
        reconnected: connection.hasConnected,
      })
      connection.hasConnected = true
    })

    connection.socket.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('[studio-realtime] message', { baseUrl: connection.baseUrl, type: data?.type })
        if (data?.type === 'pong') {
          connection.awaitingPong = false
          connection.heartbeatMisses = 0
          if (connection.heartbeatTimeout) {
            clearTimeout(connection.heartbeatTimeout)
            connection.heartbeatTimeout = null
          }
          return
        }
        handleMessage(data)
      } catch {
        // Ignore non-JSON messages.
        console.log('[studio-realtime] non-json message', { baseUrl: connection.baseUrl, data: event.data })
      }
    })

    connection.socket.addEventListener('close', () => {
      console.log('[studio-realtime] close', { baseUrl: connection.baseUrl, reason: connection.closeReason ?? 'socket-close' })
      clearHeartbeat(connection)
      connection.socket = null
      connection.closeReason = null
      connection.status = 'disconnected'
      notifyStatus()
      scheduleReconnect(connection)
    })

    connection.socket.addEventListener('error', () => {
      console.log('[studio-realtime] error', { baseUrl: connection.baseUrl })
      if (connection.socket) connection.socket.close()
    })
  }

  if (!baseUrls.length) {
    console.log('[studio-realtime] no websocket endpoints configured')
    notifyStatus()
  }

  baseUrls.forEach(url => {
    const connection = {
      baseUrl: url,
      socket: null,
      retryDelay: MIN_RECONNECT_MS,
      reconnectTimer: null,
      status: 'disconnected',
      heartbeatTimer: null,
      heartbeatTimeout: null,
      awaitingPong: false,
      heartbeatMisses: 0,
      closeReason: null,
      hasConnected: false,
      onConnected,
    }
    connections.set(url, connection)
    connect(connection)
  })

  return () => {
    stopped = true
    for (const connection of connections.values()) {
      clearReconnect(connection)
      clearHeartbeat(connection)
      if (connection.socket) {
        console.log('[studio-realtime] stop', { baseUrl: connection.baseUrl })
        connection.socket.close()
        connection.socket = null
      }
      connection.status = 'disconnected'
    }
    notifyStatus()
  }
}
